import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TokenService } from '@app/auth/auth/token.service';
import { Estado, Op } from '@app/entidades/entidades';
import { OperacionDTO, ProductoList, ProductoOperacionDTO, TipoPrecio, TipoPrecioItem } from '@app/entidades/operacion.model';
import { Usuarios } from '@app/entidades/usuario.model';
import { OperacionService } from '@app/servicios/operacion/operacion.service';
import { StorageService } from '@app/servicios/storage/storage.service';
import { UsuariosService } from '@app/servicios/usuarios/usuarios.service';
import {
  EMAIL,
  LETRAS_CON_ESPACIOS,
  SOLO_NUMEROS,
} from '@app/servicios/util.service';
import { Subscription, timer } from 'rxjs';

@Component({
  selector: 'app-finalizar',
  templateUrl: './finalizar.component.html',
  styleUrls: ['./finalizar.component.scss'],
})
export class FinalizarComponent {
  private subscriptions: Subscription = new Subscription();
  public lista: ProductoList[] = [];
  public total = 0;
  public userId = 0;
  today: number = Date.now();

  public formLogin: FormGroup;
  public formUser: FormGroup;

  public ultimoPaso = false;
  public nuevoUsuario = false;
  public alertOk = false;
  public alertBad = false;
  public pedidoCorrecto = false;

  loading = false;
  errorMessage = '';
  error = false;

  prods: any[] = [];
  loggedIn: boolean;

  unidadesSaphirus = 0;

  public costoTotal = 0;
  public gananciaTotal = 0;
  public subtotalFinal = 0;

  constructor(
    private tokenService: TokenService,
    private storageService: StorageService,
    private builder: FormBuilder,
    private userService: UsuariosService,
    private opService: OperacionService
  ) {
    this.formLogin = this.builder.group({
      user: ['', Validators.compose([Validators.required, EMAIL])],
    });
    this.formUser = this.builder.group({
      mail: ['', Validators.compose([Validators.required, EMAIL])],
      nombre: [
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(3),
          LETRAS_CON_ESPACIOS,
        ]),
      ],
      apellido: [
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(3),
          LETRAS_CON_ESPACIOS,
        ]),
      ],
      dni: [
        '',
        Validators.compose([
          Validators.required,
          SOLO_NUMEROS,
          Validators.minLength(8),
        ]),
      ],
      telefono: [
        '',
        Validators.compose([
          Validators.required,
          SOLO_NUMEROS,
          Validators.minLength(10),
        ]),
      ],
    });
  }

  ngOnInit() {
    this.consultarProductos();
    this.verificarSesion();
  }

  public verificarSesion() {
    this.loggedIn = this.tokenService.isLoggedIn() && this.tokenService.notExpired();
  }

  verificarMail() {
    this.subscriptions.add(
      this.userService
        .validarCorreo(this.formLogin.get('user').value)
        .subscribe((resp) => {
          if (resp.codigoRespuesta === '10') {
            this.userId = resp.data.usuario.id;
            this.ultimoPaso = true;
          } else {
            this.nuevoUsuario = true;
          }
        })
    );
  }

  newUsuario() {
    const user: Usuarios = this.formUser.value;
    user.id = 0;
    user.tipo = 'PUBLICO';
    user.clave = 'utunning2024';
    user.direccion = {
      calle: null,
      departamento: null,
      depto: null,
      id: null,
      localidad: null,
      numero: null,
      piso: null,
      provincia: null,
    } as any;
    user.datos_id = 0;
    user.activo = 'ACTIVO';
    user.fotos_id = 0;
    user.total_comprado_sesenta_dias = 0;
    user.numCliente = '';
    user.tipoDoc = 'DNI';

    this.subscriptions.add(
      this.userService.nuevoUsuario(user).subscribe(
        (resp) => {
          if (resp.codigoRespuesta === '10') {
            this.userId = resp.data.id;
            this.alertOk = true;
            this.ultimoPaso = true;
            this.nuevoUsuario = false;
            timer(1500).subscribe((N) => (this.alertOk = false));
          } else {
            this.error = true;
            this.errorMessage= resp.mensaje
          }
        },
        (err) => {
          this.error = true;
          this.errorMessage= err
          timer(1500).subscribe((N) => (this.alertBad = false));
        }
      )
    );
  }

  confirmarPedido() {
    this.loading = true;

    // Evitar acumulación si el usuario reintenta
    this.prods = [];
    this.armarLista();

    const operacion: OperacionDTO = {
      vendedorId: 25990,
      clienteId: this.userId,
      total: this.total,
      subtotal: this.subtotalFinal,
      costoTotal: this.costoTotal,
      gananciaTotal: this.gananciaTotal,
      operacion: Op.ECOMMERCE,
      estado: Estado.NUEVO,
      tipoPrecio: TipoPrecio.PUBLICO,
      saphirusAplicado: this.unidadesSaphirus >= 600,
      facturado: false,
      productos: this.prods,
    };

    this.opService.enviarOperacionNuevo(operacion).subscribe(
      (resp: any) => {
        this.loading = false;

        const ok = resp?.codigoRespuesta === '0' || resp?.codigoRespuesta === 'OK' || resp?.codigoRespuesta === '10';

        if (ok) {
          this.ultimoPaso = false;
          this.pedidoCorrecto = true;
          this.storageService.removeItem('lp');
          return;
        }

        this.error = true;
        this.errorMessage = resp?.mensaje ?? 'No se pudo confirmar el pedido';
        this.pedidoCorrecto = false;
      },
      (err) => {
        this.loading = false;
        this.error = true;
        this.errorMessage = err?.error?.mensaje ?? err?.message ?? 'Error al confirmar el pedido';
        this.pedidoCorrecto = false;
      }
    );
  }

  consultarProductos() {
    this.lista = this.storageService.getItem('lp');

    this.checkearSaphirus();

    if (this.unidadesSaphirus >= 600) {
      this.precioSaphirus();
    } else {
      this.calcularTotal();
    }
  }

  calcularTotal() {
    let total = 0;
    let costoTotal = 0;

    this.lista?.forEach((producto) => {
      const cantidad = +(producto.cantidad ?? 0);
      const precioPublico = +(producto.precio?.publico ?? 0);
      const costo = +(producto.precio?.costo ?? 0);
      total += precioPublico * cantidad;
      costoTotal += costo * cantidad;
    });

    this.total = total;
    this.subtotalFinal = total;
    this.costoTotal = costoTotal;
    this.gananciaTotal = this.total - this.costoTotal;
  }

  armarLista() {
    this.lista = this.storageService.getItem('lp');

    this.lista?.forEach((producto) => {
      let precio = producto.precio.publico;

      if (producto.tipo === 'SAPHIRUS' && this.unidadesSaphirus >= 600) {
        precio = producto.precio.may3;
      }

      const subtotal = (+(precio ?? 0)) * (+(producto?.cantidad ?? 0));

      const prod: ProductoOperacionDTO = {
        precio: precio,
        cantidad: producto.cantidad,
        subtotal: subtotal,
        productoId: producto.id,
        nombre: producto.nombre,
        tipo: producto.tipo,
        costoUnitario: producto.precio.costo,
        costoTotal: producto.precio.costo * producto.cantidad,
        gananciaUnitaria: precio - producto.precio.costo,
        ganancia: subtotal - producto.precio.costo * producto.cantidad,
        // opcional: si querés persistir cómo se calculó el precio
        tipoPrecioItem: producto.tipo === 'SAPHIRUS' && this.unidadesSaphirus >= 600 ? TipoPrecioItem.SAPHIRUS : TipoPrecioItem.AUTO,
      };

      this.prods.push(prod);
    });
  }

  modalError() {
    this.error = !this.error;
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  checkearSaphirus() {
    this.unidadesSaphirus = 0;
    this.lista?.forEach((pp) => {
      if ((pp.tipo ?? '').toString().toUpperCase() === 'SAPHIRUS') {
        this.unidadesSaphirus += Number(pp.cantidad ?? 0);
      }
    });
  }

  precioSaphirus() {
    let total = 0;
    let costoTotal = 0;

    this.lista?.forEach((producto) => {
      const cantidad = +(producto.cantidad ?? 0);
      const isSaphirus = (producto.tipo ?? '').toUpperCase() === 'SAPHIRUS';
      const precioUnit = isSaphirus ? +(producto.precio?.may3 ?? 0) : +(producto.precio?.publico ?? 0);
      const costo = +(producto.precio?.costo ?? 0);

      total += precioUnit * cantidad;
      costoTotal += costo * cantidad;
    });

    this.total = total;
    this.subtotalFinal = total;
    this.costoTotal = costoTotal;
    this.gananciaTotal = this.total - this.costoTotal;
  }
}
